/**
 * Shapes of the `/api/v1/suppliers` endpoint responses.
 */

/** Item from `GET /suppliers/list` — lightweight; no balance. */
export type SupplierListItem = {
    id: string;
    name: string;
    serialNumber: string | null;
    phone: string | null;
    email: string | null;
    imageUrl: string | null;
    /**
     * Hidden from pickers and from the default list, with its balance and
     * history intact (BOS-121). What a supplier with invoices, payments or
     * stock batches becomes instead of being deleted.
     */
    archived: boolean;
};

/**
 * Item from `GET /suppliers/details` — list shape + the account figures for the
 * management table.
 *
 * `balance` is `paidTotal - owedTotal`: positive = prepayment credit with the
 * supplier; negative = we owe them; zero = settled. Real since BOS-120 — the
 * server hard-coded it to `0` before that, so every row read as "Fully paid"
 * however much was owed.
 *
 * `owedTotal` sums the supplier's **received** invoices at their landed total
 * (lines + expenses), the same figure `/purchases/received-invoices` reports as
 * `totalAmount`. `openInvoiceCount` counts the ones with less allocated to them
 * than they are worth — a payment left unallocated lifts `balance` without
 * closing any single invoice.
 *
 * The `*Tetri` fields are the ones to use (BOS-162). The decimal twins are the
 * wire's own echo — what the server said, what a network log shows — and
 * nothing downstream should compute on them: `suppliersMapping` converts each
 * one at the read edge, by digit position, so the pages stay on this app's
 * integer-tetri contract.
 */
export type SupplierDetailsItem = {
    id: string;
    name: string;
    serialNumber: string | null;
    phone: string | null;
    email: string | null;
    imageUrl: string | null;
    /**
     * Hidden from pickers and from the default list, with its balance and
     * history intact (BOS-121). What a supplier with invoices, payments or
     * stock batches becomes instead of being deleted.
     */
    archived: boolean;
    /** Wire echo. Compute and format from {@link balanceTetri}. */
    balance: number;
    balanceTetri: number;
    /** Wire echo. Compute and format from {@link owedTotalTetri}. */
    owedTotal: number;
    owedTotalTetri: number;
    /** Wire echo. Compute and format from {@link paidTotalTetri}. */
    paidTotal: number;
    paidTotalTetri: number;
    /** ISO `YYYY-MM-DD`, or null if the supplier has never been paid. */
    lastPaymentDate: string | null;
    openInvoiceCount: number;
    /**
     * What has been paid to this supplier without naming an invoice (BOS-161).
     * A positive magnitude; `0` when every payment is allocated.
     *
     * This is what makes the Suppliers page and the Purchases list agree. The
     * Purchases list sums outstanding per invoice and therefore cannot see a
     * payment on account, so without this figure on screen the two differ by
     * exactly it and read as a defect:
     *
     *     -balance = Σ outstanding − unallocatedTotal
     */
    unallocatedTotal: number;
    unallocatedTotalTetri: number;
};

/** How money left the business to settle a supplier's account. */
export type SupplierPaymentMethod = 'Cash' | 'BankTransfer' | 'Card';

/** Every method, in the order the payment form offers them. */
export const SUPPLIER_PAYMENT_METHODS: SupplierPaymentMethod[] = [
    'Cash',
    'BankTransfer',
    'Card',
];

/**
 * One recorded payment, from `GET /suppliers/{id}/payments` and the create
 * response. `purchaseInvoiceId` is null for a payment on account — it moves the
 * supplier's balance without naming what it settled.
 */
export type SupplierPaymentItem = {
    id: string;
    supplierId: string;
    /** ISO `YYYY-MM-DD`. */
    date: string;
    /** Wire echo, always positive. Compute and format from {@link amountTetri}. */
    amount: number;
    amountTetri: number;
    method: SupplierPaymentMethod;
    reference: string | null;
    note: string | null;
    purchaseInvoiceId: string | null;
    purchaseInvoiceNumber: string | null;
    branchId: string | null;
    branchName: string | null;
    journalEntryId: string | null;
    createdAt: string;
};

export type SupplierPaymentsResponse = SupplierPaginated<SupplierPaymentItem>;

/** Which document a statement row describes. */
export type SupplierStatementRowKind = 'Invoice' | 'Payment';

/**
 * One dated movement on a supplier's account. `amount` carries the balance's
 * sign convention rather than a magnitude — an invoice is negative, a payment
 * positive — so summing the column reconciles against the closing balance with
 * no per-row sign rule to remember.
 */
export type SupplierStatementRow = {
    /** ISO `YYYY-MM-DD`. */
    date: string;
    kind: SupplierStatementRowKind;
    documentId: string;
    /** Invoice number, or the payment's reference. */
    reference: string | null;
    /** Wire echo. Compute and format from {@link amountTetri}. */
    amount: number;
    amountTetri: number;
    /** Wire echo. Compute and format from {@link runningBalanceTetri}. */
    runningBalance: number;
    runningBalanceTetri: number;
    /** True for a payment that names no invoice. */
    unallocated: boolean;
};

/**
 * A supplier's running account from `GET /suppliers/{id}/statement`.
 * `openingBalance + Σ rows === closingBalance` by construction.
 */
export type SupplierStatementResponse = {
    supplierId: string;
    supplierName: string;
    from: string | null;
    to: string | null;
    /** Wire echo. Compute and format from {@link openingBalanceTetri}. */
    openingBalance: number;
    openingBalanceTetri: number;
    /** Wire echo. Compute and format from {@link closingBalanceTetri}. */
    closingBalance: number;
    closingBalanceTetri: number;
    /** Invoiced within the window, as a positive magnitude. Wire echo. */
    owedTotal: number;
    owedTotalTetri: number;
    /** Paid within the window, as a positive magnitude. Wire echo. */
    paidTotal: number;
    paidTotalTetri: number;
    rows: SupplierStatementRow[];
};

/**
 * Server-paginated envelope shared by `/suppliers/list` and `/suppliers/details`
 * (BOS-81, the bos-api `PaginationResponse<T>`). `totalCount` / `totalPages`
 * describe the full filtered set; `items` is just the current page.
 */
type SupplierPaginated<TItem> = {
    items: TItem[];
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
};

export type SupplierListResponse = SupplierPaginated<SupplierListItem>;

export type SupplierDetailsResponse = SupplierPaginated<SupplierDetailsItem>;

/**
 * Response of `GET /suppliers/picker` (BOS-83) — every non-deleted supplier,
 * unpaginated, for form pickers / dropdowns and import name-matching. Item is
 * the lightweight `SupplierListItem` (no balance).
 */
export type SupplierPickerResponse = {
    items: SupplierListItem[];
};
